import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Maps from '../../utils/Maps';
import mapsIcon from '../img/mapsIcon.png';
import HeaderMain from '../Home/Headers/HeaderMain';
import Loader from '../Home/Loader';
import Footer from '../Home/Footer';

/* ══════════════════════════════════════════
   OPEN HOURS PARSING
   Turns contact_page.open_hour (e.g. [{ days: "Lun-Vie", hour: "9:00-18:00" }])
   into a Monday→Sunday schedule. "days" may be a single day, a range ("Lun-Vie"),
   or a comma-separated list ("Lun,Mie,Vie"), using either abbreviations or full names.
══════════════════════════════════════════ */
const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_ALIASES = [
    ['lun', 'lunes', 'mon', 'monday'],
    ['mar', 'martes', 'tue', 'tues', 'tuesday'],
    ['mie', 'mié', 'miercoles', 'miércoles', 'wed', 'wednesday'],
    ['jue', 'jueves', 'thu', 'thursday'],
    ['vie', 'viernes', 'fri', 'friday'],
    ['sab', 'sáb', 'sabado', 'sábado', 'sat', 'saturday'],
    ['dom', 'domingo', 'sun', 'sunday'],
];

const normalizeDayToken = (token) => token
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

const dayIndexFromToken = (token) => {
    const normalized = normalizeDayToken(token);
    return DAY_ALIASES.findIndex(aliases =>
        aliases.some(alias => normalizeDayToken(alias) === normalized)
    );
};

const expandDayRange = (daysStr) => {
    if (!daysStr) return [];
    const indices = new Set();

    daysStr.split(',').forEach(part => {
        const range = part.split(/[-–—]/).map(s => s.trim()).filter(Boolean);
        if (range.length === 2) {
            const start = dayIndexFromToken(range[0]);
            const end = dayIndexFromToken(range[1]);
            if (start === -1 || end === -1) return;
            let i = start;
            while (true) {
                indices.add(i);
                if (i === end) break;
                i = (i + 1) % 7;
            }
        } else if (range.length === 1) {
            const idx = dayIndexFromToken(range[0]);
            if (idx !== -1) indices.add(idx);
        }
    });

    return Array.from(indices);
};

const formatHourRange = (hourStr) => {
    if (!hourStr) return '';
    const matches = hourStr.match(/(\d{1,2}):(\d{2})\s*[-–—]\s*(\d{1,2}):(\d{2})/);
    if (!matches) return hourStr;

    const format12h = (h, m) => {
        const hourNum = parseInt(h, 10);
        const period = hourNum >= 12 ? 'pm' : 'am';
        const hour12 = hourNum % 12 === 0 ? 12 : hourNum % 12;
        return `${hour12}:${m} ${period}`;
    };

    return `${format12h(matches[1], matches[2])} – ${format12h(matches[3], matches[4])}`;
};

const buildSchedule = (openHour, t) => {
    const hourByDay = {};
    if (Array.isArray(openHour)) {
        openHour.forEach(entry => {
            expandDayRange(entry?.days).forEach(dayIdx => {
                hourByDay[dayIdx] = entry.hour;
            });
        });
    }

    return DAY_KEYS.map((key, idx) => {
        const hour = hourByDay[idx];
        return {
            day: t(`contact.days.${key}`),
            time: hour ? formatHourRange(hour) : t('contact.closed'),
            open: !!hour,
        };
    });
};


const Contact = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);

    const generalDataBs = localStorage.getItem("generalData");
    let contact_page_data;

    if (generalDataBs) {
        const parsedData = JSON.parse(generalDataBs);


        contact_page_data = parsedData[0].data.ecommerce_content.contact_page;

    } else {
        console.log("No hay datos almacenados bajo la clave 'generaldato'");
    }
/* ══════════════════════════════════════════
   ICONS
══════════════════════════════════════════ */
const IconLocation = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
    <circle cx="12" cy="10" r="3"/>
  </svg>
);
const IconPhone = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.72 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.63 1.2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 8.82a16 16 0 0 0 6 6l1.18-1.18a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
  </svg>
);
const IconMail = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
    <polyline points="22,6 12,13 2,6"/>
  </svg>
);
const IconMap = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21"/>
    <line x1="9" y1="3" x2="9" y2="18"/><line x1="15" y1="6" x2="15" y2="21"/>
  </svg>
);
const IconClock = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12 6 12 12 16 14"/>
  </svg>
);
const IconArrow = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"/>
    <polyline points="12 5 19 12 12 19"/>
  </svg>
);
const IconWhatsapp = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

    /* ══════════════════════════════════════════
   DATA
══════════════════════════════════════════ */
const SCHEDULE = buildSchedule(contact_page_data?.open_hour, t);

const mapLogo = localStorage.getItem('iconGeneral') || localStorage.getItem('logo') || mapsIcon;
const lat = parseFloat(contact_page_data?.direction?.lat);
const lng = parseFloat(contact_page_data?.direction?.lng);
const hasCoords = !Number.isNaN(lat) && !Number.isNaN(lng)
    && !(lat === 0 && lng === 0)
    && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
const mapAddress = hasCoords ? { lat, lng } : { lat: 8.9824, lng: -79.5199 };

    return (
        <>

            {loading && <Loader onFinish={() => setLoading(false)} />}
            {!loading &&
                (<>
                    <>
                        <div className="cp-root ecommerce-wrapper">
         

                            <HeaderMain />

                            <main className="position-relative">

                                {/* ── Hero ── */}
                                <section className="cp-hero">
                                    <div className="cp-hero-left">
                                        <p className="cp-hero-eyebrow">{t('contact.eyebrow')}</p>
                                        <h1 className="cp-hero-title">
                                            {t('contact.heroTitleLine1')}<br />
                                            <em>{t('contact.heroTitleEm')}</em>
                                        </h1>
                                    </div>
                                    <div className="cp-hero-right">
                                        <p className="cp-hero-desc">
                                            {t('contact.heroDesc')}
                                        </p>
                                        <a
                                            href={`https://wa.me/507${contact_page_data?.phone}`}
                                            className="cp-hero-cta"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <IconWhatsapp />
                                            {t('contact.whatsappCta')}
                                            <IconArrow />
                                        </a>
                                    </div>
                                </section>

                                <div className="cp-divider" />

                                {/* ── Contact Info ── */}
                                <section className="cp-info">
                                    <div className="cp-info-label">
                                        <p className="cp-section-number">{t('contact.infoSectionNumber')}</p>
                                        <h2 className="cp-section-heading">
                                            {t('contact.infoHeadingLine1')}<br />{t('contact.infoHeadingLine2')}
                                        </h2>
                                    </div>

                                    <div className="cp-cards-stack">

                                        {/* Location */}
                                        <div className="cp-info-card">
                                            <div className="cp-info-card-icon"><IconLocation /></div>
                                            <div className="cp-info-card-content">
                                                <p className="cp-info-card-label">{t('contact.locationLabel')}</p>
                                                <p className="cp-info-card-title">{t('contact.locationTitle')}</p>
                                                <p className="cp-info-card-sub">
                                                    {contact_page_data?.direction?.additional_info},{" "}
                                                    {contact_page_data?.direction?.house_num}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Phone */}
                                        <div className="cp-info-card">
                                            <div className="cp-info-card-icon"><IconPhone /></div>
                                            <div className="cp-info-card-content">
                                                <p className="cp-info-card-label">{t('contact.phoneLabel')}</p>
                                                <p className="cp-info-card-title">{t('contact.phoneTitle')}</p>
                                                <a
                                                    href={`tel:+507${contact_page_data?.phone}`}
                                                    className="cp-info-card-link"
                                                >
                                                    +(507) {contact_page_data?.phone} <IconArrow />
                                                </a>
                                            </div>
                                        </div>

                                        {/* Email */}
                                        <div className="cp-info-card">
                                            <div className="cp-info-card-icon"><IconMail /></div>
                                            <div className="cp-info-card-content">
                                                <p className="cp-info-card-label">{t('contact.emailLabel')}</p>
                                                <p className="cp-info-card-title">{t('contact.emailTitle')}</p>
                                                <a
                                                    href={`mailto:${contact_page_data?.email}`}
                                                    className="cp-info-card-link"
                                                >
                                                    {contact_page_data?.email} <IconArrow />
                                                </a>
                                            </div>
                                        </div>

                                    </div>
                                </section>

                                {/* ── Schedule ── */}
                                <section className="cp-schedule-section">
                                    <div className="cp-schedule-inner">
                                        <div className="cp-info-label">
                                            <p className="cp-section-number">{t('contact.scheduleSectionNumber')}</p>
                                            <h2 className="cp-section-heading">
                                                {t('contact.scheduleHeadingLine1')}<br />{t('contact.scheduleHeadingLine2')}
                                            </h2>
                                        </div>

                                        <div className="cp-schedule-rows">
                                            {SCHEDULE.map(({ day, time, open }) => (
                                                <div className="cp-schedule-row" key={day}>
                                                    <span className="cp-schedule-day">{day}</span>
                                                    {open ? (
                                                        <span className="cp-schedule-time">{time}</span>
                                                    ) : (
                                                        <span className="cp-schedule-badge">
                                                            <span className="cp-schedule-badge-dot" style={{ background: "#ccc" }} />
                                                            {t('contact.closed')}
                                                        </span>
                                                    )}
                                                    {open && (
                                                        <span className="cp-schedule-badge">
                                                            <span className="cp-schedule-badge-dot" />
                                                            {t('contact.open')}
                                                        </span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </section>

                                {/* ── Map ── */}
                                <section className="cp-map-section">
                                    <div className="cp-map-meta">
                                        <p className="cp-section-number">{t('contact.mapSectionNumber')}</p>
                                        <h2 className="cp-section-heading">
                                            {t('contact.mapHeadingLine1')}<br />{t('contact.mapHeadingLine2')}
                                        </h2>

                                        <div className="cp-map-address-block">
                                            <p className="cp-map-address-label">{t('contact.exactAddress')}</p>
                                            <p className="cp-map-address-text">
                                                {contact_page_data?.direction?.additional_info},{" "}
                                                {contact_page_data?.direction?.house_num}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="cp-map-frame-wrap">
                                        <Maps
                                            modo="location"
                                            logo={mapLogo}
                                            zoom={16}
                                            width="100%"
                                            height="100%"
                                            address={mapAddress}
                                        />
                                    </div>
                                </section>

                            </main>

                            <Footer />
                        </div>

                    </>

                    {contact_page_data.whatsapp_phone !== '' ? <></> : <></>}
                </>)}
        </>
    )
}

export default Contact